import React from 'react';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'title' | 'rectangular' | 'circular' | 'stat' | 'card' | 'row';
  width?: string | number;
  height?: string | number;
}

export function Skeleton({
  variant = 'text',
  width,
  height,
  className = '',
  style,
  ...props
}: SkeletonProps) {
  const variantClass =
    variant === 'text'
      ? 'pe-skeleton-text'
      : variant === 'title'
        ? 'pe-skeleton-title'
        : variant === 'circular'
          ? 'pe-skeleton-circle'
          : variant === 'stat'
            ? 'pe-skeleton-stat'
            : variant === 'card'
              ? 'pe-skeleton-card'
              : variant === 'row'
                ? 'pe-skeleton-row'
                : '';

  const inlineStyles: React.CSSProperties = {
    ...style,
    ...(width !== undefined ? { width: typeof width === 'number' ? `${width}px` : width } : {}),
    ...(height !== undefined ? { height: typeof height === 'number' ? `${height}px` : height } : {}),
  };

  return (
    <div
      className={`pe-skeleton ${variantClass} ${className}`.trim()}
      style={inlineStyles}
      aria-hidden="true"
      {...props}
    />
  );
}

/**
 * هيكل لوحة المؤشرات (Dashboard Skeleton) مطابق للأبعاد الحقيقية 100% لمنع CLS
 */
export function DashboardSkeleton() {
  return (
    <div className="pe-fade-in" aria-busy="true" aria-label="جارٍ تجهيز لوحة المؤشرات">
      {/* شبكة الإحصائيات الخمسة */}
      <div className="stat-grid pe-stagger" style={{ marginBottom: 20 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="stat pe-skeleton-stat col" style={{ justifyContent: 'center', gap: 10, padding: 16 }}>
            <Skeleton variant="text" width="40%" height={12} />
            <Skeleton variant="title" width="60%" height={26} />
            <Skeleton variant="text" width="75%" height={10} />
          </div>
        ))}
      </div>

      {/* أعمدة اللوحة الأربعة */}
      <div className="board-grid pe-stagger">
        {Array.from({ length: 4 }).map((_, colIndex) => (
          <div key={colIndex} className="board-col col" style={{ gap: 12 }}>
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
              <Skeleton variant="text" width={80} height={18} />
              <Skeleton variant="circular" width={22} height={22} />
            </div>
            {Array.from({ length: 3 }).map((_, cardIndex) => (
              <div key={cardIndex} className="pe-skeleton-card col" style={{ padding: 14, gap: 10, background: 'var(--surface)' }}>
                <Skeleton variant="text" width="85%" height={16} />
                <Skeleton variant="text" width="50%" height={12} />
                <div className="row" style={{ justifyContent: 'space-between', marginTop: 6 }}>
                  <Skeleton variant="circular" width={20} height={20} />
                  <Skeleton variant="text" width={60} height={12} />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * هيكل الجدول (Table Skeleton)
 */
export function TableSkeleton({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="card pe-fade-in" aria-busy="true">
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              {Array.from({ length: cols }).map((_, i) => (
                <th key={i}>
                  <Skeleton variant="text" width="70%" height={14} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }).map((_, r) => (
              <tr key={r}>
                {Array.from({ length: cols }).map((_, c) => (
                  <td key={c}>
                    <Skeleton variant="text" width={c === 0 ? '80%' : '50%'} height={14} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * هيكل شبكة البطاقات (Cards Grid Skeleton)
 */
export function CardsGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid pe-stagger" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="pe-skeleton-card col" style={{ padding: 16, gap: 12, background: 'var(--surface)' }}>
          <Skeleton variant="title" width="70%" height={20} />
          <Skeleton variant="text" width="90%" height={14} />
          <Skeleton variant="text" width="40%" height={12} />
        </div>
      ))}
    </div>
  );
}
